/**
 * OE Scoring Algorithm
 *
 * Maps each of the 29 survey questions to REPS quadrants with weights.
 * Weights sum to 1.0 per question.
 *
 * REPS = Reliability, Efficiency, Productivity, Safety
 */

// Question-to-REPS weight mappings
import { bandKey, scoreColor, normalizeThresholds, DEFAULT_THRESHOLD } from './bands.js';

export const QUESTION_WEIGHTS = {
  // Section 1: Drift in Perception of Risk (5 questions)
  drift_perception_q1: { R: 0.3, E: 0.1, P: 0.2, S: 0.4 },
  drift_perception_q2: { R: 0.25, E: 0.1, P: 0.2, S: 0.45 },
  drift_perception_q3: { R: 0.3, E: 0.1, P: 0.15, S: 0.45 },
  drift_perception_q4: { R: 0.2, E: 0.15, P: 0.25, S: 0.4 },
  drift_perception_q5: { R: 0.3, E: 0.15, P: 0.2, S: 0.35 },

  // Section 2: Drift in How Work is Done (5 questions)
  drift_work_q1: { R: 0.3, E: 0.2, P: 0.2, S: 0.3 },
  drift_work_q2: { R: 0.3, E: 0.2, P: 0.2, S: 0.3 },
  drift_work_q3: { R: 0.3, E: 0.15, P: 0.2, S: 0.35 },
  drift_work_q4: { R: 0.25, E: 0.25, P: 0.2, S: 0.3 },
  drift_work_q5: { R: 0.3, E: 0.2, P: 0.2, S: 0.3 },

  // Section 3: Accumulation - Traps (11 items)
  traps_time_pressure: { R: 0.2, E: 0.1, P: 0.4, S: 0.3 },
  traps_peer_pressure: { R: 0.2, E: 0.15, P: 0.3, S: 0.35 },
  traps_distractions: { R: 0.2, E: 0.15, P: 0.35, S: 0.3 },
  traps_change_off_normal: { R: 0.3, E: 0.15, P: 0.15, S: 0.4 },
  traps_multiple_tasks: { R: 0.2, E: 0.15, P: 0.4, S: 0.25 },
  traps_physical_environment: { R: 0.2, E: 0.2, P: 0.2, S: 0.4 },
  traps_overconfidence: { R: 0.25, E: 0.1, P: 0.2, S: 0.45 },
  traps_fatigue: { R: 0.3, E: 0.1, P: 0.1, S: 0.5 },
  traps_vague_guidance: { R: 0.3, E: 0.2, P: 0.2, S: 0.3 },
  traps_first_last_shift: { R: 0.25, E: 0.15, P: 0.15, S: 0.45 },
  traps_mental_stress: { R: 0.2, E: 0.1, P: 0.2, S: 0.5 },

  // Section 4: Roadblocks (4 questions)
  roadblocks_q1: { R: 0.2, E: 0.4, P: 0.3, S: 0.1 },
  roadblocks_q2: { R: 0.15, E: 0.45, P: 0.3, S: 0.1 },
  roadblocks_q3: { R: 0.2, E: 0.35, P: 0.35, S: 0.1 },
  // Communication breakdowns hit Reliability hardest (wrong info → wrong actions),
  // Safety significantly (shift-handoff blind spots), Productivity (rework / delays).
  roadblocks_q4_communication: { R: 0.3, E: 0.2, P: 0.25, S: 0.25 },

  // Section 5: Landmines (4 questions)
  landmines_q1: { R: 0.3, E: 0.2, P: 0.1, S: 0.4 },
  landmines_q2: { R: 0.35, E: 0.2, P: 0.1, S: 0.35 },
  landmines_q3: { R: 0.3, E: 0.15, P: 0.15, S: 0.4 },
  // Inaccurate labeling/signage/checklists most directly drives Reliability and Safety errors.
  landmines_q4_labeling_checklists: { R: 0.35, E: 0.15, P: 0.1, S: 0.4 },
};

/**
 * Calculate REPS scores for a single response
 * @param {Object} answers - Map of question keys to scores (1-10)
 * @returns {Object} { reliability, efficiency, productivity, safety }
 */
export function calculateResponseREPS(answers) {
  const totals = { R: 0, E: 0, P: 0, S: 0 };
  const counts = { R: 0, E: 0, P: 0, S: 0 };

  for (const [questionKey, weights] of Object.entries(QUESTION_WEIGHTS)) {
    const score = answers[questionKey];
    if (score != null && !isNaN(score)) {
      for (const [quadrant, weight] of Object.entries(weights)) {
        totals[quadrant] += score * weight;
        counts[quadrant] += weight;
      }
    }
  }

  return {
    reliability: counts.R > 0 ? totals.R / counts.R : 0,
    efficiency: counts.E > 0 ? totals.E / counts.E : 0,
    productivity: counts.P > 0 ? totals.P / counts.P : 0,
    safety: counts.S > 0 ? totals.S / counts.S : 0,
  };
}

/**
 * Aggregate REPS scores across multiple responses
 * @param {Array} responses - Array of response objects with repsScores
 * @returns {Object} Averaged REPS scores
 */
export function aggregateREPSScores(responses) {
  if (!responses || responses.length === 0) {
    return { reliability: 0, efficiency: 0, productivity: 0, safety: 0 };
  }

  const sum = { reliability: 0, efficiency: 0, productivity: 0, safety: 0 };
  for (const r of responses) {
    if (r.repsScores) {
      sum.reliability += r.repsScores.reliability || 0;
      sum.efficiency += r.repsScores.efficiency || 0;
      sum.productivity += r.repsScores.productivity || 0;
      sum.safety += r.repsScores.safety || 0;
    }
  }

  const count = responses.length;
  return {
    reliability: sum.reliability / count,
    efficiency: sum.efficiency / count,
    productivity: sum.productivity / count,
    safety: sum.safety / count,
  };
}

/** Default minimum responses needed before showing aggregate scores */
export const MIN_RESPONSE_THRESHOLD = 3;

/** Valid scope levels for aggregation */
export const SCOPE_LEVELS = ['organization', 'site', 'department', 'team'];

/**
 * Filter responses by scope level
 * @param {Array} responses - Array of response objects
 * @param {Object} scope - { type: 'organization'|'site'|'department'|'team', id: string }
 * @returns {Array} Filtered responses matching the scope
 */
export function filterResponsesByScope(responses, scope) {
  if (!responses || !Array.isArray(responses)) return [];
  if (!scope || !scope.type || scope.type === 'organization') {
    // Organization-level: include all responses
    return responses;
  }

  return responses.filter((r) => {
    switch (scope.type) {
      case 'site':
        return r.siteId === scope.id;
      case 'department':
        return r.departmentId === scope.id;
      case 'team':
        return r.teamId === scope.id;
      default:
        return true;
    }
  });
}

/**
 * Aggregate OE scores for a specific scope with minimum response threshold
 * @param {Array} responses - Array of response objects with repsScores
 * @param {Object} scope - { type: 'organization'|'site'|'department'|'team', id: string }
 * @param {number} [minResponses] - Minimum responses required (defaults to MIN_RESPONSE_THRESHOLD)
 * @returns {Object} { scores: {reliability, efficiency, productivity, safety}, responseCount, meetsThreshold }
 */
export function aggregateOEScoresForScope(responses, scope, minResponses = MIN_RESPONSE_THRESHOLD) {
  const scopedResponses = filterResponsesByScope(responses, scope);
  const responseCount = scopedResponses.length;
  const meetsThreshold = responseCount >= minResponses;

  const scores = aggregateREPSScores(scopedResponses);

  return {
    scores,
    responseCount,
    meetsThreshold,
    scope: scope || { type: 'organization', id: null },
  };
}

/**
 * Get severity level for a score
 * @param {number} score - OE score (0-10)
 * @returns {'healthy' | 'monitor' | 'warning'}
 */
export function getSeverity(score, threshold = DEFAULT_THRESHOLD) {
  return bandKey(score, threshold);
}

/**
 * Score color coding constants
 * Green = healthy (<4), Yellow = monitor (4-6), Red = warning (>6)
 */
export const SCORE_COLORS = {
  green: '#2e7d32',
  yellow: '#f9a825',
  red: '#c62828',
};

/**
 * Get the color for a given OE score
 * Green (#2e7d32) for scores below 4 (healthy)
 * Yellow (#f9a825) for scores 4-6 (monitor)
 * Red (#c62828) for scores above 6 (warning)
 *
 * @param {number} score - OE score (0-10)
 * @returns {string} Hex color string
 */
export function getScoreColor(score, threshold = DEFAULT_THRESHOLD, quadrant = null) {
  return scoreColor(score, threshold, quadrant); // one band for the whole app (P5-01)
}

/**
 * OE Element section definitions mapping section keys to their question keys
 */
export const OE_ELEMENT_SECTIONS = {
  driftPerception: ['drift_perception_q1', 'drift_perception_q2', 'drift_perception_q3', 'drift_perception_q4', 'drift_perception_q5'],
  driftWork: ['drift_work_q1', 'drift_work_q2', 'drift_work_q3', 'drift_work_q4', 'drift_work_q5'],
  traps: ['traps_time_pressure', 'traps_peer_pressure', 'traps_distractions', 'traps_change_off_normal', 'traps_multiple_tasks', 'traps_physical_environment', 'traps_overconfidence', 'traps_fatigue', 'traps_vague_guidance', 'traps_first_last_shift', 'traps_mental_stress'],
  roadblocks: ['roadblocks_q1', 'roadblocks_q2', 'roadblocks_q3', 'roadblocks_q4_communication'],
  landmines: ['landmines_q1', 'landmines_q2', 'landmines_q3', 'landmines_q4_labeling_checklists'],
};

/**
 * Calculate element breakdown scores for a single response.
 * Returns per-section per-REPS quadrant scores.
 *
 * @param {Object} answers - Map of question keys to scores (1-10)
 * @returns {Object} { driftPerception: {R,E,P,S}, driftWork: {R,E,P,S}, traps: {R,E,P,S}, roadblocks: {R,E,P,S}, landmines: {R,E,P,S} }
 */
export function calculateElementBreakdown(answers) {
  if (!answers || typeof answers !== 'object') {
    return null;
  }

  const elementScores = {};

  for (const [sectionKey, questionKeys] of Object.entries(OE_ELEMENT_SECTIONS)) {
    const totals = { R: 0, E: 0, P: 0, S: 0 };
    const counts = { R: 0, E: 0, P: 0, S: 0 };

    for (const qKey of questionKeys) {
      const score = answers[qKey];
      const weights = QUESTION_WEIGHTS[qKey];
      if (score != null && !isNaN(score) && weights) {
        for (const [quadrant, weight] of Object.entries(weights)) {
          totals[quadrant] += score * weight;
          counts[quadrant] += weight;
        }
      }
    }

    elementScores[sectionKey] = {
      R: counts.R > 0 ? totals.R / counts.R : 0,
      E: counts.E > 0 ? totals.E / counts.E : 0,
      P: counts.P > 0 ? totals.P / counts.P : 0,
      S: counts.S > 0 ? totals.S / counts.S : 0,
    };
  }

  return elementScores;
}

/**
 * Calculate aggregate element breakdown scores across multiple responses.
 * Each OE element (driftPerception, driftWork, traps, roadblocks, landmines)
 * gets per-REPS quadrant scores averaged across all responses.
 *
 * @param {Array} responses - Array of response objects with answers
 * @returns {Object|null} elementScores map or null if no responses
 */
export function aggregateElementScores(responses) {
  if (!responses || responses.length === 0) {
    return null;
  }

  const elementScores = {};

  for (const [sectionKey, questionKeys] of Object.entries(OE_ELEMENT_SECTIONS)) {
    const sectionTotals = { R: 0, E: 0, P: 0, S: 0 };
    const sectionCounts = { R: 0, E: 0, P: 0, S: 0 };

    for (const response of responses) {
      const answers = response.answers || {};
      for (const qKey of questionKeys) {
        const score = answers[qKey];
        const weights = QUESTION_WEIGHTS[qKey];
        if (score != null && !isNaN(score) && weights) {
          for (const [quadrant, weight] of Object.entries(weights)) {
            sectionTotals[quadrant] += score * weight;
            sectionCounts[quadrant] += weight;
          }
        }
      }
    }

    elementScores[sectionKey] = {
      R: sectionCounts.R > 0 ? sectionTotals.R / sectionCounts.R : 0,
      E: sectionCounts.E > 0 ? sectionTotals.E / sectionCounts.E : 0,
      P: sectionCounts.P > 0 ? sectionTotals.P / sectionCounts.P : 0,
      S: sectionCounts.S > 0 ? sectionTotals.S / sectionCounts.S : 0,
    };
  }

  return elementScores;
}

/**
 * Check thresholds and generate warnings
 * @param {Object} scores - REPS scores
 * @param {Object} thresholds - Per-quadrant thresholds
 * @returns {Array} Array of warning objects
 */
export function checkThresholds(scores, thresholds) {
  const warnings = [];
  const quadrants = ['reliability', 'efficiency', 'productivity', 'safety'];
  const t = normalizeThresholds(thresholds); // accepts {R,E,P,S} too (P4-21)

  for (const q of quadrants) {
    const score = scores[q] || 0;
    const threshold = t[q] || 6;
    if (score > threshold) {
      warnings.push({
        quadrant: q,
        score,
        threshold,
        message: `${q.charAt(0).toUpperCase() + q.slice(1)} score (${score.toFixed(1)}) exceeds threshold (${threshold})`,
      });
    }
  }

  return warnings;
}
